/**
 * Facility-scoped feature-flag and category hooks from Firestore
 * healthcare_facility config loaded into Redux.
 *
 * @module useOncoemrTasksEnabled
 */
import { useSelector } from "react-redux";
import { isHealthFacilityFeatureEnabled } from "../data-model/healthFacility";
import { RootState } from "../redux/store/store";

/**
 * Returns whether OncoEMR tasks UI and inbox polling are enabled for the
 * logged-in user's healthcare facility (`enable_oncoemr_tasks` in `feature_config` / facility doc).
 *
 * @returns `true` when tasks tab, notification bell, and polling are active.
 */
export const useOncoemrTasksEnabled = (): boolean =>
  useSelector((state: RootState) =>
    isHealthFacilityFeatureEnabled(
      state.singleHealthFacilityDetails.data,
      "enable_oncoemr_tasks",
    ),
  );

/**
 * Returns whether Candid auth-letter upload UI is enabled for the
 * logged-in user's healthcare facility (`enable_candid_auth_letter_upload`
 * in `feature_config` / facility doc).
 *
 * @returns `true` when the Candid Update tab / upload affordances are active.
 */
export const useCandidUpdateEnabled = (): boolean =>
  useSelector((state: RootState) =>
    isHealthFacilityFeatureEnabled(
      state.singleHealthFacilityDetails.data,
      "enable_candid_auth_letter_upload",
    ),
  );

/**
 * Returns the logged-in healthcare facility's Firestore `category` from Redux.
 *
 * @returns Facility category string, or `undefined` when the facility doc has
 *   not loaded or the field is unset
 */
export const useFacilityCategory = (): string | undefined =>
  useSelector(
    (state: RootState) => state.singleHealthFacilityDetails.data?.category,
  );
